// Engagement-tier derivation for the admin Accounts tab.
//
// Tiers are computed client-side from the usageAnalytics docs (written by the
// /admin/updateUsageAnalytics cloud function) plus account state the tab
// already loads. Nothing is stored — tweak the definitions here and the view
// updates on next render.

// A tier-1 ("active caller") account has a multi-person call within this window.
export const TIER1_WINDOW_DAYS = 60;

export const TIERS = {
  1: {
    label: 'T1',
    name: 'Active caller',
    description: `Multi-person call in the last ${TIER1_WINDOW_DAYS} days`,
    badgeClass: 'bg-green-600 text-white hover:bg-green-600',
    chipOnClass: 'bg-green-600 text-white border-green-600',
    chipOffClass: 'text-green-700 border-green-300 hover:bg-green-50',
  },
  2: {
    label: 'T2',
    name: 'Lapsed caller',
    description: `Has done multi-person calls, but none in the last ${TIER1_WINDOW_DAYS} days`,
    badgeClass: 'bg-amber-500 text-white hover:bg-amber-500',
    chipOnClass: 'bg-amber-500 text-white border-amber-500',
    chipOffClass: 'text-amber-700 border-amber-300 hover:bg-amber-50',
  },
  3: {
    label: 'T3',
    name: 'Solo only',
    description: 'Has used the Hearth, but never a multi-person call',
    badgeClass: 'bg-blue-500 text-white hover:bg-blue-500',
    chipOnClass: 'bg-blue-500 text-white border-blue-500',
    chipOffClass: 'text-blue-700 border-blue-300 hover:bg-blue-50',
  },
  4: {
    label: 'T4',
    name: 'No sessions',
    description: 'Device signed in, but never joined a Higlo',
    badgeClass: 'bg-gray-500 text-white hover:bg-gray-500',
    chipOnClass: 'bg-gray-500 text-white border-gray-500',
    chipOffClass: 'text-gray-700 border-gray-300 hover:bg-gray-50',
  },
  5: {
    label: 'T5',
    name: 'Not set up',
    description: 'Account or device not set up yet',
    badgeClass: 'bg-transparent text-muted-foreground border border-gray-300 hover:bg-transparent',
    chipOnClass: 'bg-gray-300 text-gray-900 border-gray-400',
    chipOffClass: 'text-gray-500 border-gray-200 hover:bg-gray-50',
  },
};

export const TIER_NUMBERS = [1, 2, 3, 4, 5];

/**
 * Derive the engagement tier for one account.
 *
 * account        — accountsInfo doc ({ families: [...] , ... })
 * usageAnalytics — { [familyId]: usageAnalytics doc } (may be empty / missing entries)
 * signedInCount  — number of the account's families with a signed-in device
 * nowMs          — injectable clock for tests (defaults to Date.now())
 *
 * Aggregates across the account's families, taking the best (most engaged)
 * signal. Evaluated top-down; first match wins.
 */
export function deriveTier(account, usageAnalytics, signedInCount, nowMs = Date.now()) {
  const tier1CutoffMs = nowMs - TIER1_WINDOW_DAYS * 24 * 3600 * 1000;

  let anyMultiPerson = false;
  let anySessions = false;

  for (const fid of account?.families || []) {
    const a = usageAnalytics?.[fid];
    if (!a) continue;
    if (a.multiPersonCallCount > 0) anyMultiPerson = true;
    if (a.totalSessions > 0) anySessions = true;
  }

  const lastMultiMs = accountLastMultiPersonCallMs(account, usageAnalytics);
  if (lastMultiMs != null && lastMultiMs >= tier1CutoffMs) return 1;
  if (anyMultiPerson) return 2;
  if (anySessions) return 3;
  if ((signedInCount || 0) > 0) return 4;
  return 5;
}

/**
 * All-time most recent multi-person call across the account's families, as
 * epoch ms (or null). Uses the summary field, NOT the retained calls array, so
 * it still answers for accounts whose call records were truncated by the
 * sweep's MAX_RETAINED_CALLS doc-size cap (the "Last call" column's fallback).
 */
export function accountLastMultiPersonCallMs(account, usageAnalytics) {
  let lastMs = null;
  for (const fid of account?.families || []) {
    const t = toMs(usageAnalytics?.[fid]?.lastMultiPersonCallAt);
    if (t != null && (lastMs === null || t > lastMs)) lastMs = t;
  }
  return lastMs;
}

// "1h 05m" / "12m" from seconds
export function formatCallDuration(seconds) {
  const totalMin = Math.round((seconds || 0) / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// Firestore Timestamp | epoch ms | {seconds} -> epoch ms (or null)
export function toMs(v) {
  if (v == null) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v === 'number') return v;
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  return null;
}

/**
 * Merge an account's families' call lists into one deduped, newest-first list.
 * Two families of the same account in the same call produce the same call in
 * both usageAnalytics docs — dedupe on iglooId + start time.
 * Returns [{ startMs, durationSeconds, peakFamilies, iglooId }].
 */
export function accountCalls(account, usageAnalytics) {
  const seen = new Set();
  const out = [];
  for (const fid of account?.families || []) {
    const a = usageAnalytics?.[fid];
    for (const call of a?.calls || []) {
      const startMs = toMs(call.startedAt);
      if (startMs == null) continue;
      const key = `${call.iglooId}|${startMs}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        startMs,
        durationSeconds: call.durationSeconds || 0,
        peakFamilies: call.peakFamilies || 2,
        iglooId: call.iglooId,
      });
    }
  }
  out.sort((a, b) => b.startMs - a.startMs);
  return out;
}
